import { useCallback, useEffect, useRef, useState } from "react";
/****************************************************************
 * useWakeLock custom hook
 * Hook that manages a Screen Wake Lock for a single component.
 * Provides start/stop functions and a boolean state for UI.
 * Handles visibility and unmount cleanup to avoid leaks.
 ****************************************************************/
const useWakeLock = () => {
  const wakeLockRef = useRef(null);
  const shouldBeActiveRef = useRef(false);
  const isRequestingRef = useRef(false);
  const mountedRef = useRef(true);
  const [isWakeLockActive, setIsWakeLockActive] = useState(false);

  
  /****************************************************************
   * enableWakeLock
   * Requests a screen wake lock if supported and visible.
   * Guards against concurrent requests, hidden documents, and SSR.
   * Attaches a 'release' handler that clears state and can re-acquire.
   ****************************************************************/
  const enableWakeLock = useCallback(async () => {
    shouldBeActiveRef.current = true;

    if (!("wakeLock" in navigator)) {
      return false;
    }

    if (document.visibilityState !== "visible") {
      return false;
    }

    if (wakeLockRef.current) {
       /* istanbul ignore next -- defensive: unreachable after unmount clears ref */
      if (mountedRef.current) setIsWakeLockActive(true);
      return true;
    }

    if (isRequestingRef.current) {
      return false;
    }

    isRequestingRef.current = true;

    try {
      const lock = await navigator.wakeLock.request("screen");
      wakeLockRef.current = lock;

      if (mountedRef.current) {
        setIsWakeLockActive(true);
      }

      const onRelease = () => {
        
        if (wakeLockRef.current === lock) {
          wakeLockRef.current = null;
          /* istanbul ignore next -- defensive: unreachable after unmount clears ref */
          if (mountedRef.current) setIsWakeLockActive(false);
        }

        if (
          shouldBeActiveRef.current &&
          document.visibilityState === "visible"
        ) {
           /* istanbul ignore next */
          enableWakeLock().catch(() => {});
        }
      };

      lock.addEventListener("release", onRelease);
      return true;
    } catch (err) {
      if (err?.name !== "NotAllowedError") {
        console.error("Wake lock request failed:", err);
      }
      return false;
    } finally {
      isRequestingRef.current = false;
    }
  }, []);
 /****************************************************************
   * disableWakeLock
   * Marks intent as inactive and releases any held wake lock.
   * Clears local refs and updates component state for UI.
   * Safe to call repeatedly; no-op if no lock is held.
   ****************************************************************/
  const disableWakeLock = useCallback(async () => {
    shouldBeActiveRef.current = false;

    const lock = wakeLockRef.current;
    wakeLockRef.current = null;

    if (mountedRef.current) {
      setIsWakeLockActive(false);
    }

    if (lock) {
      try {
        await lock.release();
      } catch (_) {
        // ignore
      }
    }
  }, []);

  useEffect(() => {
    const handleVisibility = () => {
     
      if (
        document.visibilityState === "visible" &&
        shouldBeActiveRef.current &&
        !wakeLockRef.current
      ) {
         /* istanbul ignore next */
        enableWakeLock().catch(() => {});
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [enableWakeLock]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      shouldBeActiveRef.current = false;

      const lock = wakeLockRef.current;
      wakeLockRef.current = null;

      if (lock) {
        
        /* istanbul ignore next */
        lock.release().catch(() => {});
      }
    };
  }, []);

  return {
    isWakeLockActive,
    enableWakeLock,
    disableWakeLock,
  };
};
export default useWakeLock;